import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface EmailConfirmationPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const EmailConfirmationPage: React.FC<EmailConfirmationPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      
      // Vérifier s'il y a des erreurs dans l'URL
      const urlParams = new URLSearchParams(window.location.hash.substring(1));
      const error = urlParams.get('error');
      const errorCode = urlParams.get('error_code');
      
      if (error) {
        
        if (errorCode === 'otp_expired') {
          showToast('Le lien de confirmation a expiré. Veuillez vous connecter.', 'error');
          navigate('/login');
        } else {
          showToast('Erreur lors de la confirmation. Veuillez vous connecter.', 'error');
          navigate('/login');
        }
        return;
      }
      
      
      if (loading) return;
      
      if (!user) {
        showToast('Veuillez vous connecter après avoir confirmé votre email.', 'error');
        navigate('/login');
        return;
      }
      
      // Vérifier si l'utilisateur a un profil
      try {
        const { data: profileData } = await api.profiles.getByUserId(user.id);
        
        if (profileData) {
          showToast('Email confirmé avec succès !', 'success');
          navigate('/profile');
        } else {
          showToast('Email confirmé ! Créons maintenant votre profil.', 'success');
          navigate('/create-profile');
        }
      } catch (err) {
        showToast('Email confirmé ! Créons votre profil.', 'success');
        navigate('/create-profile');
      }
    };
    
    // Exécuter immédiatement
    handleEmailConfirmation();
  }, [user, loading, navigate, showToast]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-envelope-check text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Email confirmé !
          </h2>
          <p className="text-gray-600">
            Redirection vers votre profil...
          </p>
        </div>

        {/* Contenu */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002c46] mx-auto"></div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <i className="fas fa-check text-green-500 mr-2"></i>
                Email confirmé avec succès
              </p>
              <p className="text-sm text-gray-600">
                <i className="fas fa-spinner fa-spin text-[#002c46] mr-2"></i>
                Redirection en cours...
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
              <p className="text-sm text-green-800">
                <i className="fas fa-check-circle text-green-600 mr-2"></i>
                Redirection automatique dans quelques secondes
              </p>
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            La redirection prend trop de temps ?
          </p>
          <button
            onClick={() => navigate('/login')}
            className="text-[#002c46] hover:text-[#001a2a] font-medium text-sm"
          >
            <i className="fas fa-sign-in-alt mr-1"></i>
            Aller à la page de connexion
          </button>
        </div>
      </div>
    </div>
  );
};
